import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX
import { getCurrentGameState } from '@Managers/Redux/AppState/AppState';
import { useAppSelector } from '@Managers/Redux/Hooks';
import { Button } from '@UILayer/Inputs/Button';

import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { DOM_ID } from '@UILayer/UILayer.types';

// IMAGES
import nextIcon from './icon_next.svg';
import playIcon from './icon_play.svg';
import playActiveIcon from './icon_play_active.svg';
import prevIcon from './icon_prev.svg';

const InGameHistoryPanelNavigation: React.FC<{ currentIndex: number; totalSteps: number }> = ({ currentIndex, totalSteps }) => {
    const [state, setState] = useReducer((state: { navBtnActive: boolean; duringSpin: boolean }, newState: any) => ({ ...state, ...newState }), {
        navBtnActive: true,
        duringSpin: false
    });
    const { t } = useTranslation();
    const gameStoreCurrentGameState = useAppSelector(getCurrentGameState);

    const btnCN = cn(styles.inGameHistoryNavBtn, state.navBtnActive ? styles.active : styles.disabled, totalSteps !== 1 ? styles.active : styles.disabled);

    useEffect(() => {
        let navBtnActive = true;
        let duringSpin = gameStoreCurrentGameState !== EGameStates.IDLE_STATE ? true : false;

        if (duringSpin) navBtnActive = false;
        if (totalSteps === 1) navBtnActive = false;

        setState({ navBtnActive: navBtnActive, duringSpin: duringSpin });
    }, [gameStoreCurrentGameState]);

    function previousStep() {
        if (currentIndex > 0 && state.navBtnActive && !state.duringSpin) {
            EventsCenter.emit(EGameEvents.INGAME_HISTORY_INDEX_CHANGE, { index: currentIndex - 1 });
        }
    }

    function nextStep() {
        if (currentIndex < totalSteps - 1 && state.navBtnActive && !state.duringSpin) {
            EventsCenter.emit(EGameEvents.INGAME_HISTORY_INDEX_CHANGE, { index: currentIndex + 1 });
        }
    }

    function playGameStep() {
        if (!state.duringSpin) {
            EventsCenter.emit(EGameEvents.INGAME_HISTORY_REPLAY_SPIN, { index: currentIndex });
        }
    }

    return (
        <>
            <div className={styles.inGameHistoryPanel_navigation}>
                <Button
                    id={DOM_ID.BUTTON_PREV_INGAME_HISTORY_PANEL}
                    description={t('INGAME_HISTORY_PANEL_PREV_BUTTON_WCA_DESCRIPTION')}
                    className={cn(btnCN, currentIndex === 0 ? styles.disabled : false)}
                    onChange={() => previousStep()}
                    uiFreezeCheck={false}
                >
                    <img src={prevIcon} />
                </Button>
                <Button
                    id={DOM_ID.BUTTON_PLAY_ROUNDS_INGAME_HISTORY_PANEL}
                    description={t('INGAME_HISTORY_PANEL_PLAY_ROUNDS_BUTTON_WCA_DESCRIPTION')}
                    className={styles.inGameHistoryNavBtn}
                    onChange={() => playGameStep()}
                    uiFreezeCheck={false}
                >
                    <img src={state.duringSpin ? playActiveIcon : playIcon} />
                </Button>
                <div
                    className={styles.inGameHistoryPanel_stepIndex}
                    tabIndex={0}
                    aria-description={t('INGAME_HISTORY_PANEL_INDEX_NOTE')}
                    aria-valuetext={`${currentIndex} / ${{ totalSteps }}`}
                >
                    <span className={styles.inGameHistoryPanel_label}>
                        {currentIndex + 1} / {totalSteps}
                    </span>
                </div>
                <Button
                    id={DOM_ID.BUTTON_NEXT_INGAME_HISTORY_PANEL}
                    description={t('INGAME_HISTORY_PANEL_NEXT_BUTTON_WCA_DESCRIPTION')}
                    className={cn(btnCN, currentIndex + 1 === totalSteps ? styles.disabled : false)}
                    onChange={() => nextStep()}
                    uiFreezeCheck={false}
                >
                    <img src={nextIcon} />
                </Button>
            </div>
        </>
    );
};

export default InGameHistoryPanelNavigation;
